// src/annihilation/SkillTreeView.jsx
// ============================================================
//  ANNIHILATION — the skill tree room (The Trainer, kept by
//  Sigfer; Caelarch's Affinity offer lives here for now —
//  roadmap §4.6, reversible display decision).
//
//  R-UNIVERSAL honored: this component decides NOTHING about
//  legality. Every spend, refund, and Affinity commitment
//  round-trips through treeEngine; this file only renders the
//  engine's answers and persists the resulting state.
//
//  DERIVE, DON'T STORE: level derives from xp (stats.levelForXp),
//  points available derive from level + spent map. The save is
//  only the engine state, written as the skillTrees field of
//  annihilation___character (already-whitelisted node — no new
//  Firebase paths introduced here).
//
//  Skill points are scarce and PERMANENT. The refund rail is
//  Sigfer's one-per-village-visit (two with Awoken's
//  Self-Mastery); the Bonfire's rest resets the allowance.
//
//  ⚑⚑ BATCH GATE-1 (caP'n, Jul 25 2026) ⚑⚑
//  This room IS Gate A. Every engine call now carries the
//  character's MAX AENERGY POOL, and the engine answers
//  `aenergy_locked` where it used to answer `tier_locked`.
//
//  > A Beb whose Chao caps at 60 cannot buy a 200-Chao node,
//  > and no amount of hoarded skill points will open it.
//
//  Locked nodes print their REQUIREMENT ("◆ 200 Chao"), not a
//  tier name — the player is never told "you're not high
//  enough," they're told "you cannot hold this yet," which is a
//  different sentence and a better one. Caelarch's oath moved
//  to the same gate: it is priced in Anima, not in tiers, and
//  every being clears it within a level or two of the Ascent on
//  its own growth curve.
// ============================================================

import React, { useMemo, useState } from "react";
import { getBeing } from "./beings";
import {
  levelForXp,
  tierForLevel,
  statsAtLevel,
  aenergyPool,
  CREST_TO_XP_RATE,
  fillToNextLevel,
  xpForCrests,
} from "./stats";
import {
  canSpendNode,
  spendNode,
  canRefundNode,
  refundNode,
  commitAffinity,
  pointsAvailable,
  pointsSpent,
  aenergyShortfall,
  describeAenergyReq,
  AFFINITY_COMMIT_REQ,
  NODE_TYPES,
  TREE_IDS,
} from "./treeEngine";
import { treesForCharacter, normalizeTreeState } from "./trees";
import { saveCharacter, deltaBloodMoney, deltaXp } from "./profileWrites";

// ------------------------------------------------------------
// Display strings only (vyren pattern) — engine reasons → words.
// ------------------------------------------------------------
const REASONS = {
  already_owned: "Already yours.",
  no_points: "No skill points free.",
  fork_locked: "Its fork-twin is chosen. The path is closed.",
  // ⚑ GATE-1: `tier_locked` is dead — the engine cannot return it
  // any more. Kept in the table so an old save or an old build
  // never renders a bare reason code at caP'n.
  tier_locked: "A higher tier is required.",
  aenergy_locked: "You cannot hold this yet.",
  missing_prereq: "An earlier node must be taken first.",
  cluster_investment: "More points must live in this cluster first.",
  wrong_affinity: "Not your Affinity.",
  not_owned: "Not owned.",
  refund_spent: "Sigfer has already refunded this visit. Rest, then return.",
  has_dependents: "Other nodes you own stand on this one.",
  cluster_gate_break: "Removing this would break a cluster gate you rely on.",
  unknown_node: "Unknown node.",
  already_committed: "An Affinity is already sworn.",
};

const TYPE_GLYPH = {
  [NODE_TYPES.PASSIVE]: "○",
  [NODE_TYPES.ACTIVE]: "◆",
  [NODE_TYPES.FORK]: "⬘",
  [NODE_TYPES.CAPSTONE]: "★",
  [NODE_TYPES.SCALING]: "◎",
};

// Caelarch's presentation — flavor for the eight, keyed loosely:
// ids come from affinities.js at runtime; misses fall back to a
// prettified id so a rename never blanks the offer.
const AFFINITY_FLAVOR = {
  holy: { flavor: "Light, protection, consecration", hook: "Defensive, supportive, boss-focused" },
  awoken: { flavor: "Enlightenment, self-knowledge", hook: "Damage scales with investment" },
  evil: { flavor: "Malice, manipulation, corruption", hook: "Debuff-heavy, sacrifice-powered" },
  enlightened: { flavor: "Psychic mastery", hook: "Chao-regen and cooldown specialist" },
  ancient_sorcery: { flavor: "Blood magic, ritual, the old ways", hook: "Anima-cost powered, high risk high reward" },
  shadow_gambling: { flavor: "Dice-roll, luck, the gambler's courage", hook: "Chance-based mega-effects" },
  omnia: { flavor: "Destruction, unmaking", hook: "Pure damage amplification" },
  nullus: { flavor: "Negation, void, nothing", hook: "Prevent and nullify rather than overpower" },
};

const prettify = (id) =>
  String(id || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export default function SkillTreeView({ uid, character, bloodMoney, onBack }) {
  const being = getBeing(character?.beingId);
  const { level } = levelForXp(character?.xp || 0);
  const tier = tierForLevel(level);

  // GATE A — the MAX pool. Everything this character could ever
  // hold at this level, before gear, boosts or Heresy. Derived,
  // never stored (stats.js is the one source).
  const pool = useMemo(
    () => aenergyPool(statsAtLevel(character?.beingId, level), level),
    [character?.beingId, level]
  );
  // One ctx object so no call site can forget to pass the pool.
  const ctx = useMemo(
    () => ({ level, beingId: character?.beingId, pool }),
    [level, character?.beingId, pool]
  );

  // Local engine state, seeded from the save; every successful
  // engine op replaces it AND persists. Firebase's live listener
  // will echo the same state back — no divergence window matters
  // because this component is the lane's only writer.
  const [state, setState] = useState(() =>
    normalizeTreeState(character?.skillTrees)
  );
  const [tab, setTab] = useState(TREE_IDS.LINEAGE);
  const [notice, setNotice] = useState(null);
  const [offerFocus, setOfferFocus] = useState(null); // affinity being browsed pre-commit

  const tabs = useMemo(
    () => treesForCharacter(character?.beingId, state),
    [character?.beingId, state]
  );
  const activeTab = tabs.find((t) => t.key === tab) || tabs[0];

  // Awoken's Self-Mastery doubles Sigfer's refund allowance.
  // ⚑ Display-string match (node name) — the one place the vyren
  // pattern bends, flagged in the roadmap; hardening pass should
  // give the node a machine flag in affinities.js instead.
  const maxRefunds = useMemo(() => {
    const owned = Object.keys(state.spent || {});
    const selfMastery = tabs
      .flatMap((t) => t.nodes)
      .find((n) => n.name === "Self-Mastery" && state.spent[n.id]);
    return owned.length && selfMastery ? 2 : 1;
  }, [state, tabs]);

  const persist = async (next) => {
    setState(next);
    try {
      await saveCharacter(uid, { skillTrees: next });
    } catch (e) {
      console.warn("[annihilation] skillTrees save failed", e);
      setNotice("The Trainer's ledger didn't take the mark — try again.");
    }
  };

  const trySpend = (node, nodes) => {
    const res = spendNode(state, node, nodes, ctx);
    if (!res.ok) return setNotice(REASONS[res.reason] || res.reason);
    setNotice(null);
    persist(res.state);
  };

  const tryRefund = (node, nodes) => {
    const res = refundNode(state, node, nodes, { maxRefunds });
    if (!res.ok) return setNotice(REASONS[res.reason] || res.reason);
    setNotice(null);
    persist(res.state);
  };

  const tryCommit = (affinityId) => {
    const res = commitAffinity(state, affinityId, ctx);
    if (!res.ok) return setNotice(REASONS[res.reason] || res.reason);
    setNotice(null);
    setOfferFocus(null);
    persist(res.state);
  };

  // ---------------------------------------------------------
  // THE WELL — Blood Crests → Renown (XP). caP'n's ruling:
  // crests level you up; they never buy skill points directly.
  // Both writes are delta transactions; we spend FIRST (the
  // transaction rejects atomically if you're short) and grant
  // XP only on a committed spend — the conservative direction
  // (a failed XP write after a committed spend costs one tithe,
  // never mints free Renown). Same grant-before-flag spirit as
  // the rest of the lane. Optimistic local mirrors keep the
  // panel responsive; the live profile listener reconciles.
  // ---------------------------------------------------------
  const [crestsLocal, setCrestsLocal] = useState(null); // null = use prop
  const [xpBonusLocal, setXpBonusLocal] = useState(0); // optimistic XP added this visit
  const [welling, setWelling] = useState(false);
  const [wellNote, setWellNote] = useState(null);

  // Effective values: prop is the source of truth; local overlays
  // reflect purchases the listener hasn't echoed back yet.
  const crests =
    crestsLocal != null
      ? crestsLocal
      : typeof bloodMoney === "number"
      ? bloodMoney
      : 0;
  const effectiveXp = (character?.xp || 0) + xpBonusLocal;

  const temper = async (crestCost) => {
    if (welling) return;
    const cost = Math.max(0, Math.floor(crestCost));
    if (cost <= 0) return;
    if (crests < cost) {
      setWellNote("Not enough Blood Crests. Go and earn them.");
      return;
    }
    setWelling(true);
    setWellNote(null);
    try {
      const spend = await deltaBloodMoney(uid, -cost);
      if (!spend.ok) {
        setWellNote("Not enough Blood Crests. Go and earn them.");
        setWelling(false);
        return;
      }
      // spend committed — mirror the balance and grant the Renown
      setCrestsLocal(
        typeof spend.balance === "number" ? spend.balance : crests - cost
      );
      const gained = xpForCrests(cost);
      await deltaXp(uid, gained);
      setXpBonusLocal((b) => b + gained);
      setWellNote(`+${gained.toLocaleString()} Renown tithed.`);
    } catch (e) {
      console.warn("[annihilation] Well tithe failed", e);
      setWellNote("The Well didn't take — try again.");
    } finally {
      setWelling(false);
    }
  };

  if (!being) {
    return (
      <Shell onBack={onBack}>
        <div style={S.pendingPanel}>No character found. Transcend first.</div>
      </Shell>
    );
  }

  const avail = pointsAvailable(state, level);

  return (
    <Shell onBack={onBack}>
      {/* ---------------- header ---------------- */}
      <div style={S.kicker}>The Trainer · kept by Sigfer</div>
      <h1 style={S.title}>{being.lineage}</h1>
      <div style={S.subline}>
        Level {level} · {prettify(tier)} tier
        {state.affinityId ? <> · {prettify(state.affinityId)} Affinity</> : null}
      </div>
      <div style={S.pointsRow}>
        <span style={S.pointsBig}>{avail}</span>
        <span style={S.pointsLabel}>
          point{avail === 1 ? "" : "s"} free · {pointsSpent(state)} spent ·
          refunds this visit {state.refundsUsedThisVisit}/{maxRefunds}
        </span>
      </div>

      {/* ---------------- THE WELL (Blood Crests → Renown) ------- */}
      <WellPanel
        crests={crests}
        xp={effectiveXp}
        busy={welling}
        note={wellNote}
        onTemper={temper}
      />

      {/* ---------------- tabs ---------------- */}
      <div style={S.tabRow}>
        {tabs.map((t) => (
          <button
            key={t.key}
            style={{ ...S.tab, ...(t.key === activeTab.key ? S.tabActive : null) }}
            onClick={() => {
              setTab(t.key);
              setNotice(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {notice ? <div style={S.notice}>{notice}</div> : null}

      {/* ---------------- body ---------------- */}
      {activeTab.pending ? (
        <div style={S.pendingPanel}>{activeTab.pending}</div>
      ) : activeTab.key === TREE_IDS.AFFINITY && !state.affinityId ? (
        <CaelarchOffer
          nodes={activeTab.nodes}
          level={level}
          pool={pool}
          focus={offerFocus}
          onFocus={setOfferFocus}
          onCommit={tryCommit}
        />
      ) : (
        <NodeClusters
          tabDef={activeTab}
          state={state}
          ctx={ctx}
          onSpend={trySpend}
          onRefund={tryRefund}
        />
      )}
    </Shell>
  );
}

// ============================================================
//  THE WELL — Sigfer lets you tither Blood Crests into Renown.
//  Crests LEVEL YOU UP (they never buy skill points directly —
//  points stay scarce and level-gated). "Fill to next level"
//  buys exactly the remainder; the bundles are for partial buys.
//  Pure display + button wiring — all legality/writes live above.
// ============================================================
function WellPanel({ crests, xp, busy, note, onTemper }) {
  const { xpNeeded, crestCost, level } = fillToNextLevel(xp);
  const { intoLevel, toNext } = levelForXp(xp || 0);
  const pct = toNext > 0 ? Math.min(100, Math.round((intoLevel / toNext) * 100)) : 0;

  // Bundles — fixed Renown chunks for topping off between levels.
  const BUNDLES = [
    { label: "+100", crests: 10 },
    { label: "+500", crests: 50 },
  ];

  const canFill = xpNeeded > 0 && crests >= crestCost;

  return (
    <div style={S.well}>
      <div style={S.wellHead}>
        <span style={S.wellTitle}>The Well</span>
        <span style={S.wellCrests}>🩸 {crests.toLocaleString()} Blood Crests</span>
      </div>

      <div style={S.wellBarTrack}>
        <div style={{ ...S.wellBarFill, width: `${pct}%` }} />
      </div>
      <div style={S.wellProgress}>
        Level {level} · {intoLevel.toLocaleString()} / {toNext.toLocaleString()} Renown
        {" "}
        ({CREST_TO_XP_RATE} Renown per Crest)
      </div>

      <div style={S.wellBtnRow}>
        <button
          style={{ ...S.wellFill, ...(canFill && !busy ? null : S.wellBtnOff) }}
          disabled={!canFill || busy}
          onClick={() => onTemper(crestCost)}
        >
          {xpNeeded <= 0
            ? "Ready to ascend — spend a point below"
            : `Fill to Level ${level + 1} — ${crestCost.toLocaleString()} 🩸`}
        </button>
        {BUNDLES.map((b) => {
          const affordable = crests >= b.crests;
          return (
            <button
              key={b.label}
              style={{ ...S.wellBundle, ...(affordable && !busy ? null : S.wellBtnOff) }}
              disabled={!affordable || busy}
              onClick={() => onTemper(b.crests)}
            >
              {b.label} Renown · {b.crests} 🩸
            </button>
          );
        })}
      </div>

      {note ? <div style={S.wellNote}>{note}</div> : null}
    </div>
  );
}

// ============================================================
//  Caelarch's offer — the eight, before commitment. Browsing is
//  free; the oath is permanent and only the engine may grant it.
// ============================================================
function CaelarchOffer({ nodes, level, pool, focus, onFocus, onCommit }) {
  const affinityIds = useMemo(() => {
    const seen = [];
    for (const n of nodes) {
      if (n.affinityId && !seen.includes(n.affinityId)) seen.push(n.affinityId);
    }
    return seen;
  }, [nodes]);

  // ⚑ GATE-1: the oath is priced in AENERGY, not in tiers.
  // Caelarch does not ask what you are called. He asks whether
  // there is enough of you to hold an oath this size.
  const short = aenergyShortfall(pool, AFFINITY_COMMIT_REQ);
  const oathOpen = !short;

  return (
    <div>
      <div style={S.caelarch}>
        {oathOpen
          ? "Caelarch regards you. “There is enough of you now. Eight roads stand open. Walk one, and the other seven close forever. Look as long as you like — the oath waits.”"
          : `Caelarch regards you, but does not speak of oaths. “Not yet. An oath of this size needs ${short.need} ${short.label} to sit in, and you carry ${Math.round(short.have)}.” You stand at level ${level}. Browse freely.`}
      </div>
      <div style={S.offerGrid}>
        {affinityIds.map((id) => {
          const fl = AFFINITY_FLAVOR[id] || {};
          const active = focus === id;
          return (
            <button
              key={id}
              style={{ ...S.offerCard, ...(active ? S.offerCardActive : null) }}
              onClick={() => onFocus(active ? null : id)}
            >
              <div style={S.offerName}>{prettify(id)}</div>
              {fl.flavor ? <div style={S.offerFlavor}>{fl.flavor}</div> : null}
              {fl.hook ? <div style={S.offerHook}>{fl.hook}</div> : null}
            </button>
          );
        })}
      </div>

      {focus ? (
        <div style={S.offerDetail}>
          <div style={S.offerDetailHead}>{prettify(focus)} — the fifteen</div>
          {nodes
            .filter((n) => n.affinityId === focus)
            .map((n) => (
              <div key={n.id} style={S.offerNodeLine}>
                <span style={S.glyph}>{TYPE_GLYPH[n.type] || "○"}</span>{" "}
                <span style={S.offerNodeName}>{n.name}</span> — {n.effect}
              </div>
            ))}
          <button
            style={{
              ...S.commit,
              ...(oathOpen ? null : S.commitLocked),
            }}
            disabled={!oathOpen}
            onClick={() => onCommit(focus)}
          >
            {oathOpen
              ? `Swear the ${prettify(focus)} oath — permanent`
              : `The oath needs ${describeAenergyReq(AFFINITY_COMMIT_REQ)}`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
//  Node clusters — the working view for Lineage / Universal /
//  Tome / committed Affinity. Grouped by cluster, each node a
//  card that renders the engine's verdict.
// ============================================================
function NodeClusters({ tabDef, state, ctx, onSpend, onRefund }) {
  const { tree, nodes } = tabDef;
  const clusters = useMemo(() => {
    const order = [];
    const byId = {};
    for (const n of nodes) {
      const c = n.cluster || "general";
      if (!byId[c]) {
        byId[c] = [];
        order.push(c);
      }
      byId[c].push(n);
    }
    return order.map((c) => ({
      id: c,
      label: tree?.clusters?.[c]?.name || prettify(c),
      focus: tree?.clusters?.[c]?.focus || null,
      nodes: byId[c],
    }));
  }, [nodes, tree]);

  return (
    <div>
      {clusters.map((c) => (
        <div key={c.id} style={S.cluster}>
          <div style={S.clusterHead}>
            {c.label}
            {c.focus ? <span style={S.clusterFocus}> — {c.focus}</span> : null}
          </div>
          {c.nodes.map((node) => (
            <NodeCard
              key={node.id}
              node={node}
              nodes={nodes}
              state={state}
              ctx={ctx}
              onSpend={onSpend}
              onRefund={onRefund}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function NodeCard({ node, nodes, state, ctx, onSpend, onRefund }) {
  const owned = Boolean(state.spent[node.id]);
  const spendCheck = owned ? null : canSpendNode(state, node, nodes, ctx);
  const refundCheck = owned ? canRefundNode(state, node, nodes, {}) : null;
  // ⚑ GATE-1: the requirement is printed on the node itself, so a
  // player can SEE the far thing and want it. That wanting is the
  // whole point — "you cannot hold this yet" is an invitation.
  const reqLabel = describeAenergyReq(node.aenergyReq);

  return (
    <div
      style={{
        ...S.node,
        ...(owned ? S.nodeOwned : spendCheck?.ok ? S.nodeOpen : S.nodeLocked),
      }}
    >
      <div style={S.nodeTop}>
        <span style={S.glyph}>{TYPE_GLYPH[node.type] || "○"}</span>
        <span style={S.nodeName}>{node.name}</span>
        {reqLabel ? <span style={S.nodeTag}>◆ {reqLabel}</span> : null}
        {node.forkWith ? <span style={S.nodeTag}>fork</span> : null}
        {node.restGated ? <span style={S.nodeTag}>rest-gated</span> : null}
      </div>
      <div style={S.nodeEffect}>{node.effect}</div>
      <div style={S.nodeActions}>
        {owned ? (
          <>
            <span style={S.ownedMark}>owned</span>
            <button style={S.refundBtn} onClick={() => onRefund(node, nodes)}>
              refund (Sigfer)
            </button>
          </>
        ) : spendCheck?.ok ? (
          <button style={S.spendBtn} onClick={() => onSpend(node, nodes)}>
            spend 1 point
          </button>
        ) : (
          <span style={S.lockReason}>
            {REASONS[spendCheck?.reason] || spendCheck?.reason}
            {spendCheck?.shortfall
              ? ` (${spendCheck.shortfall.need} ${spendCheck.shortfall.label} — you hold ${Math.round(
                  spendCheck.shortfall.have
                )})`
              : ""}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  Shell — the room chrome, matching App.js's VillageRoom look.
// ============================================================
function Shell({ children, onBack }) {
  return (
    <div style={S.void}>
      <div style={S.frame}>
        {children}
        <button style={S.back} onClick={onBack}>
          ← back to the square
        </button>
      </div>
    </div>
  );
}

const gold = "#e8a33d";
const crest = "#e86a5c"; // the Blood Crest accent (matches App.js currency pill)
const S = {
  void: {
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 50% 30%, #14101c 0%, #0a0810 55%, #050408 100%)",
    display: "flex",
    justifyContent: "center",
    padding: "24px 12px",
    boxSizing: "border-box",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  // ---- The Well (Blood Crests → Renown) --------------------
  well: {
    border: "1px solid rgba(232,106,92,0.35)",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 18,
    background: "rgba(232,106,92,0.05)",
  },
  wellHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 10,
  },
  wellTitle: {
    color: crest,
    fontSize: 15,
    letterSpacing: "0.06em",
    fontWeight: 600,
  },
  wellCrests: { color: crest, fontSize: 12.5, letterSpacing: "0.04em" },
  wellBarTrack: {
    height: 7,
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    overflow: "hidden",
    marginBottom: 6,
  },
  wellBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #b8443a, #e86a5c)",
    transition: "width 0.3s ease",
  },
  wellProgress: {
    color: "#9a8d74",
    fontSize: 11.5,
    fontStyle: "italic",
    marginBottom: 12,
  },
  wellBtnRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  wellFill: {
    flex: "1 1 100%",
    background: "rgba(232,106,92,0.14)",
    border: "1px solid rgba(232,106,92,0.7)",
    color: crest,
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: 13.5,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.03em",
  },
  wellBundle: {
    flex: "1 1 0",
    background: "rgba(232,106,92,0.06)",
    border: "1px solid rgba(232,106,92,0.4)",
    color: "#d8968c",
    padding: "9px 10px",
    borderRadius: 8,
    fontSize: 12.5,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  wellBtnOff: {
    opacity: 0.4,
    cursor: "default",
    borderColor: "rgba(109,95,71,0.4)",
    color: "#6d5f47",
    background: "rgba(109,95,71,0.06)",
  },
  wellNote: {
    color: crest,
    fontSize: 11.5,
    fontStyle: "italic",
    marginTop: 10,
  },
  frame: {
    maxWidth: 680,
    width: "100%",
    border: "1px solid rgba(232,163,61,0.35)",
    borderRadius: 12,
    padding: "28px 20px",
    background: "rgba(12,10,18,0.88)",
    boxShadow: "0 0 60px rgba(232,163,61,0.08), inset 0 0 40px rgba(0,0,0,0.5)",
    textAlign: "center",
    alignSelf: "flex-start",
  },
  kicker: {
    color: "#8a7a5c",
    fontSize: 12,
    letterSpacing: "0.35em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  title: {
    color: gold,
    fontSize: 28,
    margin: "0 0 4px",
    fontWeight: 600,
    textShadow: "0 0 24px rgba(232,163,61,0.35)",
  },
  subline: { color: "#b5a284", fontSize: 13, fontStyle: "italic", marginBottom: 14 },
  pointsRow: { marginBottom: 18 },
  pointsBig: { color: gold, fontSize: 26, fontWeight: 700, marginRight: 8 },
  pointsLabel: { color: "#8a7a5c", fontSize: 12 },
  tabRow: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  tab: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.3)",
    color: "#b5a284",
    padding: "7px 16px",
    borderRadius: 999,
    fontSize: 12.5,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.06em",
  },
  tabActive: {
    border: `1px solid ${gold}`,
    color: gold,
    background: "rgba(232,163,61,0.1)",
  },
  notice: {
    color: "#e86a5c",
    fontSize: 12.5,
    border: "1px solid rgba(232,106,92,0.35)",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: 14,
    background: "rgba(232,106,92,0.06)",
  },
  pendingPanel: {
    color: "#9a8d74",
    fontSize: 13.5,
    fontStyle: "italic",
    padding: "26px 10px",
  },
  // clusters + nodes
  cluster: { marginBottom: 20, textAlign: "left" },
  clusterHead: {
    color: gold,
    fontSize: 14,
    letterSpacing: "0.08em",
    borderBottom: "1px solid rgba(232,163,61,0.25)",
    paddingBottom: 6,
    marginBottom: 10,
  },
  clusterFocus: { color: "#8a7a5c", fontSize: 11.5, fontStyle: "italic" },
  node: {
    border: "1px solid rgba(232,163,61,0.18)",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 8,
    background: "rgba(0,0,0,0.25)",
  },
  nodeOwned: {
    border: "1px solid rgba(143,209,138,0.5)",
    background: "rgba(143,209,138,0.05)",
  },
  nodeOpen: { border: "1px solid rgba(232,163,61,0.5)" },
  nodeLocked: { opacity: 0.62 },
  nodeTop: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  glyph: { color: gold, fontSize: 13 },
  nodeName: { color: "#e6dcc8", fontSize: 13.5, fontWeight: 600 },
  nodeTag: {
    color: "#8a7a5c",
    fontSize: 9.5,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    border: "1px solid rgba(138,122,92,0.4)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  nodeEffect: { color: "#b5a284", fontSize: 12, lineHeight: 1.5, margin: "6px 0 8px" },
  nodeActions: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  spendBtn: {
    background: "rgba(232,163,61,0.12)",
    border: `1px solid ${gold}`,
    color: gold,
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  refundBtn: {
    background: "transparent",
    border: "1px solid rgba(232,106,92,0.5)",
    color: "#e86a5c",
    padding: "5px 12px",
    borderRadius: 6,
    fontSize: 11.5,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  ownedMark: {
    color: "#8fd18a",
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  lockReason: { color: "#6d5f47", fontSize: 11.5, fontStyle: "italic" },
  // Caelarch
  caelarch: {
    color: "#d8cdb8",
    fontSize: 13.5,
    lineHeight: 1.6,
    fontStyle: "italic",
    marginBottom: 16,
    textAlign: "left",
  },
  offerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 8,
    marginBottom: 14,
  },
  offerCard: {
    background: "rgba(232,163,61,0.05)",
    border: "1px solid rgba(232,163,61,0.25)",
    borderRadius: 8,
    padding: "10px 8px",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "center",
  },
  offerCardActive: {
    border: `1px solid ${gold}`,
    background: "rgba(232,163,61,0.12)",
  },
  offerName: { color: gold, fontSize: 13.5, fontWeight: 600, marginBottom: 4 },
  offerFlavor: { color: "#b5a284", fontSize: 10.5, fontStyle: "italic", marginBottom: 3 },
  offerHook: { color: "#8a7a5c", fontSize: 10 },
  offerDetail: {
    border: "1px solid rgba(232,163,61,0.2)",
    borderRadius: 8,
    padding: "12px 14px",
    background: "rgba(0,0,0,0.3)",
    textAlign: "left",
    marginBottom: 6,
  },
  offerDetailHead: {
    color: gold,
    fontSize: 13,
    letterSpacing: "0.08em",
    marginBottom: 10,
  },
  offerNodeLine: { color: "#b5a284", fontSize: 11.5, lineHeight: 1.55, marginBottom: 6 },
  offerNodeName: { color: "#e6dcc8", fontWeight: 600 },
  commit: {
    display: "block",
    width: "100%",
    marginTop: 12,
    background: "rgba(232,106,92,0.12)",
    border: "1px solid rgba(232,106,92,0.7)",
    color: "#e86a5c",
    padding: "11px 16px",
    borderRadius: 8,
    fontSize: 13.5,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  commitLocked: {
    border: "1px solid rgba(138,122,92,0.4)",
    color: "#6d5f47",
    background: "transparent",
    cursor: "default",
  },
  back: {
    background: "transparent",
    border: "1px solid rgba(232,163,61,0.5)",
    color: gold,
    padding: "10px 22px",
    borderRadius: 8,
    fontSize: 13.5,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "0.05em",
    marginTop: 10,
  },
};
